import { type RefObject, useEffect, useRef } from 'react';

import {
  type MarqueeState,
  type ReadableStore,
  marqueeBorderStyle,
  marqueeRect,
} from '@frontend/viewer-core';

import {
  MARQUEE_CROSSING_FILL_COLOR,
  MARQUEE_CROSSING_STROKE_COLOR,
  MARQUEE_FILL_COLOR,
  MARQUEE_STROKE_COLOR,
} from '../constants';
import {
  bindOverlayFrame,
  create,
  setAttributes,
  show,
} from '../utils/toolUtils';

export type MarqueeOverlayDeps = {
  marquee: ReadableStore<MarqueeState | null>;
};

/**
 * Screen-space marquee while a selection drag is in flight.
 *
 * Already in container pixels — pan does not move the box. SVG rather than
 * WebGL because the rest of the tool overlays share that host model, and
 * imperative so a drag never commits React.
 */
export const useDxfMarqueeOverlay = ({
  marquee,
}: MarqueeOverlayDeps): RefObject<SVGSVGElement> => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const rect = create('rect');
    setAttributes(rect, { 'stroke-width': 1, rx: 1 });
    svg.appendChild(rect);

    const draw = () => {
      const marqueeState = marquee.getState();
      show(rect, Boolean(marqueeState) && !marqueeState?.isAborted);
      if (!marqueeState || marqueeState.isAborted) {
        return;
      }

      const [minX, minY, maxX, maxY] = marqueeRect(marqueeState);
      // Dashed *and* green means "anything it touches". The dash is the AutoCAD
      // convention, so both cues say the same thing rather than one qualifying
      // the other — and both follow the drag's live direction, so dragging back
      // across the anchor restyles the box as it changes meaning.
      const isCrossing = marqueeBorderStyle(marqueeState) === 'dashed';
      setAttributes(rect, {
        x: minX,
        y: minY,
        width: Math.max(0, maxX - minX),
        height: Math.max(0, maxY - minY),
        fill: isCrossing ? MARQUEE_CROSSING_FILL_COLOR : MARQUEE_FILL_COLOR,
        stroke: isCrossing
          ? MARQUEE_CROSSING_STROKE_COLOR
          : MARQUEE_STROKE_COLOR,
        'stroke-dasharray': isCrossing ? '4 3' : 'none',
      });
    };

    return bindOverlayFrame({
      draw,
      subscriptions: [marquee.subscribe],
      onCleanup: () => svg.replaceChildren(),
    });
  }, [marquee]);

  return svgRef;
};
